import { useState, useEffect, useMemo } from 'react';
import { getSuppliers, getPurchaseRecords, savePurchaseRecord, getProfitStats } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Truck, DollarSign, Save, Calendar, Plus, Trash2, Building2, ArrowLeft, Check, ChevronRight, Receipt, Hash } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const PAYMENT_METHODS = ['当日現金', '翌日現金', '翌週掛け', '月掛け', '15日掛け'];
const isCashPayment = (method) => (method || '').includes('現金');

export const Profit = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseAmounts, setPurchaseAmounts] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const [expenseItems, setExpenseItems] = useState([]); // 経費精算
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editingCustom, setEditingCustom] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [inputValues, setInputValues] = useState({ amount_8: '', amount_10: '', payment_method: '', payment_status: '', tax_type: '税込み' });
  const [newCustomName, setNewCustomName] = useState('');
  const [newExpenseName, setNewExpenseName] = useState('');
  // 保存後のFC/FK番号表示用
  const [savedRefNumbers, setSavedRefNumbers] = useState({});
  const [showRefDialog, setShowRefDialog] = useState(null); // { name, ref_number, payment_method }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (suppliers.length > 0) {
      fetchDayData();
    }
  }, [selectedDate, suppliers]);

  const fetchData = async () => {
    try {
      const suppliersRes = await getSuppliers();
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchDayData = async () => {
    try {
      const statsRes = await getProfitStats(selectedDate);
      setStats(statsRes.data);

      const purchaseRes = await getPurchaseRecords({ date: selectedDate });
      const record = purchaseRes.data[0];

      if (record) {
        const amounts = {};
        const customs = [];
        const expenses = [];
        const refs = {};

        record.items.forEach(item => {
          if (item.ref_number) {
            refs[item.supplier_id] = item.ref_number;
          }
          if (item.is_expense) {
            expenses.push({
              id: item.supplier_id,
              name: item.supplier_name,
              amount_8: item.amount_8 || 0,
              amount_10: item.amount_10 || 0,
              payment_method: item.payment_method || '',
              payment_status: item.payment_status || '',
              ref_number: item.ref_number || '',
            });
          } else if (item.supplier_id.startsWith('custom-')) {
            customs.push({
              id: item.supplier_id,
              name: item.supplier_name,
              amount_8: item.amount_8 || 0,
              amount_10: item.amount_10 || 0,
              payment_method: item.payment_method || '',
              payment_status: item.payment_status || '',
              ref_number: item.ref_number || '',
            });
          } else {
            amounts[item.supplier_id] = {
              amount_8: item.amount_8 || 0,
              amount_10: item.amount_10 || 0,
              payment_method: item.payment_method || '',
              payment_status: item.payment_status || '',
              ref_number: item.ref_number || '',
            };
          }
        });

        setPurchaseAmounts(amounts);
        setCustomItems(customs);
        setExpenseItems(expenses);
        setSavedRefNumbers(refs);
      } else {
        setPurchaseAmounts({});
        setCustomItems([]);
        setExpenseItems([]);
        setSavedRefNumbers({});
      }
    } catch (error) {
      console.error('Error fetching day data:', error);
    }
  };

  const openSupplierInput = (supplier) => {
    setSelectedSupplier(supplier);
    setEditingCustom(null);
    setEditingExpense(null);
    const existing = purchaseAmounts[supplier.id];
    setInputValues({
      amount_8: existing?.amount_8 || '',
      amount_10: existing?.amount_10 || '',
      payment_method: existing?.payment_method || supplier.payment_method || '',
      payment_status: existing?.payment_status || '',
    });
  };

  const openCustomInput = (item) => {
    setEditingCustom(item.id);
    setSelectedSupplier(null);
    setEditingExpense(null);
    setInputValues({
      amount_8: item.amount_8 || '',
      amount_10: item.amount_10 || '',
      payment_method: item.payment_method || '',
      payment_status: item.payment_status || '',
    });
    setNewCustomName(item.name);
  };

  const addAndEditCustom = () => {
    const newId = `custom-${Date.now()}`;
    setEditingCustom(newId);
    setSelectedSupplier(null);
    setEditingExpense(null);
    setInputValues({ amount_8: '', amount_10: '', payment_method: '', payment_status: '', tax_type: '税込み' });
    setNewCustomName('');
  };

  const openExpenseInput = (item) => {
    setEditingExpense(item.id);
    setSelectedSupplier(null);
    setEditingCustom(null);
    setInputValues({
      amount_8: item.amount_8 || '',
      amount_10: item.amount_10 || '',
      payment_method: '',
      payment_status: '',
    });
    setNewExpenseName(item.name);
  };

  const addAndEditExpense = () => {
    const newId = `expense-${Date.now()}`;
    setEditingExpense(newId);
    setSelectedSupplier(null);
    setEditingCustom(null);
    setInputValues({ amount_8: '', amount_10: '', payment_method: '', payment_status: '', tax_type: '税込み' });
    setNewExpenseName('');
  };

  // バックエンドに現在の状態を保存する共通関数
  const saveToBackend = async (overrideAmounts, overrideCustom, overrideExpense) => {
    const amounts = overrideAmounts || purchaseAmounts;
    const customs = overrideCustom || customItems;
    const expenses = overrideExpense || expenseItems;
    
    const supplierItems = suppliers.map(s => {
      const a = amounts[s.id] || { amount_8: 0, amount_10: 0, payment_method: '', payment_status: '' };
      return { supplier_id: s.id, supplier_name: s.name, amount: (a.amount_8 || 0) + (a.amount_10 || 0), amount_8: a.amount_8 || 0, amount_10: a.amount_10 || 0, payment_method: a.payment_method || s.payment_method || '', payment_status: a.payment_status || '', is_expense: false };
    });
    const customSave = customs.filter(i => i.name.trim()).map(i => ({
      supplier_id: i.id, supplier_name: i.name, amount: (i.amount_8 || 0) + (i.amount_10 || 0), amount_8: i.amount_8 || 0, amount_10: i.amount_10 || 0, payment_method: i.payment_method || '', payment_status: i.payment_status || '', is_expense: false,
    }));
    const expenseSave = expenses.filter(i => i.name.trim()).map(i => ({
      supplier_id: i.id, supplier_name: i.name, amount: (i.amount_8 || 0) + (i.amount_10 || 0), amount_8: i.amount_8 || 0, amount_10: i.amount_10 || 0, payment_method: '経費', payment_status: '', is_expense: true,
    }));

    const res = await savePurchaseRecord({
      date: selectedDate,
      items: [...supplierItems, ...customSave, ...expenseSave]
    });

    const savedItems = res.data?.items || [];
    const newRefs = {};
    savedItems.forEach(item => {
      if (item.ref_number && item.amount > 0) {
        newRefs[item.supplier_id] = item.ref_number;
      }
    });
    setSavedRefNumbers(newRefs);
    return res;
  };

  // 仕入れ先の入力を削除（バックエンドにも反映）
  const deleteSupplierEntry = async (supplierId) => {
    const next = { ...purchaseAmounts };
    delete next[supplierId];
    setPurchaseAmounts(next);
    try {
      await saveToBackend(next, customItems, expenseItems);
      toast.success('削除しました');
    } catch { toast.error('削除に失敗しました'); }
  };

  const deleteCustomEntry = async (itemId) => {
    const next = customItems.filter(i => i.id !== itemId);
    setCustomItems(next);
    try {
      await saveToBackend(purchaseAmounts, next, expenseItems);
      toast.success('削除しました');
    } catch { toast.error('削除に失敗しました'); }
  };

  const deleteExpenseEntry = async (itemId) => {
    const next = expenseItems.filter(i => i.id !== itemId);
    setExpenseItems(next);
    try {
      await saveToBackend(purchaseAmounts, customItems, next);
      toast.success('削除しました');
    } catch { toast.error('削除に失敗しました'); }
  };

  const saveInput = async () => {
    const rawA8 = parseFloat(inputValues.amount_8) || 0;
    const rawA10 = parseFloat(inputValues.amount_10) || 0;
    // 税抜きの場合は税込みに変換して保存
    const a8 = inputValues.tax_type === '税抜き' ? Math.round(rawA8 * 1.08) : rawA8;
    const a10 = inputValues.tax_type === '税抜き' ? Math.round(rawA10 * 1.10) : rawA10;
    const pm = inputValues.payment_method || '';
    const ps = inputValues.payment_status || '';

    if (a8 === 0 && a10 === 0) {
      toast.error('金額を入力してください');
      return;
    }

    // 現金系の場合、支払状況を必須にする
    const isExpenseMode = !!editingExpense;
    if (!isExpenseMode && isCashPayment(pm) && !ps) {
      toast.error('支払状況を選択してください');
      return;
    }

    // ローカルステートを先に更新
    let currentSupplierId = null;
    let currentPaymentMethod = pm;
    let updatedAmounts = { ...purchaseAmounts };
    let updatedCustom = [...customItems];
    let updatedExpense = [...expenseItems];

    if (selectedSupplier) {
      currentSupplierId = selectedSupplier.id;
      updatedAmounts[selectedSupplier.id] = { amount_8: a8, amount_10: a10, payment_method: pm, payment_status: ps };
      setPurchaseAmounts(updatedAmounts);
    } else if (editingCustom) {
      if (!newCustomName.trim()) { toast.error('仕入れ先名を入力してください'); return; }
      currentSupplierId = editingCustom;
      const exists = updatedCustom.find(i => i.id === editingCustom);
      if (exists) {
        updatedCustom = updatedCustom.map(i => i.id === editingCustom ? { ...i, name: newCustomName, amount_8: a8, amount_10: a10, payment_method: pm, payment_status: ps } : i);
      } else {
        updatedCustom = [...updatedCustom, { id: editingCustom, name: newCustomName, amount_8: a8, amount_10: a10, payment_method: pm, payment_status: ps }];
      }
      setCustomItems(updatedCustom);
    } else if (editingExpense) {
      if (!newExpenseName.trim()) { toast.error('経費名を入力してください'); return; }
      currentSupplierId = editingExpense;
      const exists = updatedExpense.find(i => i.id === editingExpense);
      if (exists) {
        updatedExpense = updatedExpense.map(i => i.id === editingExpense ? { ...i, name: newExpenseName, amount_8: a8, amount_10: a10 } : i);
      } else {
        updatedExpense = [...updatedExpense, { id: editingExpense, name: newExpenseName, amount_8: a8, amount_10: a10 }];
      }
      setExpenseItems(updatedExpense);
    }

    // 即座にバックエンドへ保存してFC/FK番号を取得
    setSaving(true);
    try {
      const res = await saveToBackend(updatedAmounts, updatedCustom, updatedExpense);

      const savedItems = res.data?.items || [];
      let refForCurrent = null;
      savedItems.forEach(item => {
        if (item.ref_number && item.amount > 0 && item.supplier_id === currentSupplierId) {
          refForCurrent = item.ref_number;
        }
      });

      // 登録した項目の番号をモーダルで表示（現金系の場合のみ）
      if (refForCurrent && isCashPayment(currentPaymentMethod)) {
        setShowRefDialog(refForCurrent);
      }
    } catch (error) {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }

    setSelectedSupplier(null);
    setEditingCustom(null);
    setEditingExpense(null);
  };

  const cancelInput = () => {
    setSelectedSupplier(null);
    setEditingCustom(null);
    setEditingExpense(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveToBackend(purchaseAmounts, customItems, expenseItems);
      toast.success('仕入れ額を保存しました');
      fetchDayData();
    } catch (error) {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const supplierTotal = Object.values(purchaseAmounts).reduce(
    (sum, v) => sum + (v.amount_8 || 0) + (v.amount_10 || 0), 0
  );
  const customTotal = customItems.reduce(
    (sum, item) => sum + (item.amount_8 || 0) + (item.amount_10 || 0), 0
  );
  const expenseTotal = expenseItems.reduce(
    (sum, item) => sum + (item.amount_8 || 0) + (item.amount_10 || 0), 0
  );
  const totalPurchase = supplierTotal + customTotal + expenseTotal;

  const enteredSuppliers = useMemo(() => {
    return suppliers.filter(s => {
      const a = purchaseAmounts[s.id];
      return a && ((a.amount_8 || 0) > 0 || (a.amount_10 || 0) > 0);
    });
  }, [suppliers, purchaseAmounts]);

  const unentered = useMemo(() => {
    return suppliers.filter(s => {
      const a = purchaseAmounts[s.id];
      return !a || ((a.amount_8 || 0) === 0 && (a.amount_10 || 0) === 0);
    });
  }, [suppliers, purchaseAmounts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // ========== FC/FK番号モーダル ==========
  if (showRefDialog) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="ref-number-dialog">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-xl">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Receipt className="h-8 w-8 text-green-700" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">登録完了</h2>
          <p className="text-sm text-slate-500 mb-5">レシートに以下の番号を記入してください</p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl py-5 px-6 mb-6">
            <p className="text-4xl font-bold font-mono tracking-wider text-slate-800" data-testid="ref-number-display">
              {showRefDialog}
            </p>
          </div>
          <Button
            onClick={() => { setShowRefDialog(null); fetchDayData(); }}
            className="w-full bg-slate-800 hover:bg-slate-900 text-white py-3 text-base rounded-xl"
            data-testid="ref-dialog-close"
          >
            OK
          </Button>
        </div>
      </div>
    );
  }

  // ========== 入力画面 ==========
  if (selectedSupplier || editingCustom || editingExpense) {
    const isExpense = !!editingExpense;
    const title = selectedSupplier ? selectedSupplier.name : isExpense ? (newExpenseName || '新規経費') : (newCustomName || '新規仕入れ先');
    return (
      <div className="space-y-6 max-w-lg mx-auto" data-testid="purchase-input-form">
        <button onClick={cancelInput} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors">
          <ArrowLeft className="h-4 w-4" /> 戻る
        </button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {isExpense ? <Receipt className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
              {title}
            </CardTitle>
            <p className="text-sm text-slate-500">
              {format(new Date(selectedDate), 'M月d日(E)', { locale: ja })}の{isExpense ? '経費' : '仕入れ額'}
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* 税タイプの注意書き */}
            {inputValues.tax_type === '税抜き' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                税抜き金額を入力 → 自動で税込みに変換して保存されます
              </div>
            )}

            {/* カスタム仕入れ先 / 経費の名前入力 */}
            {(editingCustom || editingExpense) && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{isExpense ? '経費名' : '仕入れ先名'}</Label>
                <Input
                  value={isExpense ? newExpenseName : newCustomName}
                  onChange={(e) => isExpense ? setNewExpenseName(e.target.value) : setNewCustomName(e.target.value)}
                  placeholder={isExpense ? '経費名を入力' : '仕入れ先名を入力'}
                  autoFocus
                  data-testid="custom-name-input"
                />
              </div>
            )}

            {/* 支払方法（経費以外） */}
            {!isExpense && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">支払方法</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="input-payment-method">
                  {PAYMENT_METHODS.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setInputValues(prev => ({ ...prev, payment_method: m, payment_status: isCashPayment(m) ? prev.payment_status : '' }))}
                      className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        inputValues.payment_method === m
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 支払い済み/未払い（現金系のみ） */}
            {!isExpense && isCashPayment(inputValues.payment_method) && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">支払状況</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="input-payment-status">
                  {['支払い済み', '未払い'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setInputValues(prev => ({ ...prev, payment_status: s }))}
                      className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        inputValues.payment_status === s
                          ? s === '支払い済み' ? 'border-green-500 bg-green-50 text-green-700' : 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 税抜き・税込み選択 */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">伝票の金額</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="input-tax-type">
                {['税込み', '税抜き'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setInputValues(prev => ({ ...prev, tax_type: t }))}
                    className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                      inputValues.tax_type === t
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">8% 税率分{inputValues.tax_type === '税抜き' ? '（税抜き）' : ''}</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-lg">¥</span>
                <Input
                  type="number"
                  value={inputValues.amount_8}
                  onChange={(e) => setInputValues(prev => ({ ...prev, amount_8: e.target.value }))}
                  placeholder="0"
                  className="text-right text-lg"
                  autoFocus={!editingCustom && !editingExpense}
                  data-testid="input-amount-8"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">10% 税率分{inputValues.tax_type === '税抜き' ? '（税抜き）' : ''}</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-lg">¥</span>
                <Input
                  type="number"
                  value={inputValues.amount_10}
                  onChange={(e) => setInputValues(prev => ({ ...prev, amount_10: e.target.value }))}
                  placeholder="0"
                  className="text-right text-lg"
                  data-testid="input-amount-10"
                />
              </div>
            </div>

            <div className="pt-3 border-t">
              {inputValues.tax_type === '税抜き' && ((parseFloat(inputValues.amount_8) || 0) + (parseFloat(inputValues.amount_10) || 0)) > 0 && (
                <div className="flex justify-between items-center text-sm text-slate-500 mb-2">
                  <span>税抜き合計</span>
                  <span className="font-mono">
                    ¥{((parseFloat(inputValues.amount_8) || 0) + (parseFloat(inputValues.amount_10) || 0)).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm text-slate-500 mb-1">
                <span>{inputValues.tax_type === '税抜き' ? '税込み合計（保存される金額）' : '合計'}</span>
                <span className="text-xl font-bold text-slate-900">
                  ¥{(() => {
                    const r8 = parseFloat(inputValues.amount_8) || 0;
                    const r10 = parseFloat(inputValues.amount_10) || 0;
                    if (inputValues.tax_type === '税抜き') {
                      return (Math.round(r8 * 1.08) + Math.round(r10 * 1.10)).toLocaleString();
                    }
                    return (r8 + r10).toLocaleString();
                  })()}
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={cancelInput} className="flex-1">キャンセル</Button>
              <Button onClick={saveInput} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="register-purchase-btn">
                <Check className="h-4 w-4 mr-1" /> 登録
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========== メイン画面 ==========
  return (
    <div className="space-y-6" data-testid="profit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">仕入れ登録</h1>
          <p className="text-slate-500 mt-1">仕入れ先を選んで金額を入力</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />日付:
          </Label>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" data-testid="profit-date-input" />
        </div>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-blue-700">
              {format(new Date(selectedDate), 'M月d日(E)', { locale: ja })}の実績
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> 売上</p>
                <p className="text-2xl font-bold text-green-600">¥{(stats?.day?.sales || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> 仕入れ</p>
                <p className="text-2xl font-bold text-red-600">¥{(stats?.day?.purchase || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3" /> 利益</p>
                <p className={`text-2xl font-bold ${(stats?.day?.profit || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ¥{(stats?.day?.profit || 0).toLocaleString()}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><Truck className="h-3 w-3" /> 配送件数</p>
                <p className="text-2xl font-bold text-slate-700">{stats?.day?.delivery_count || 0}件</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-purple-700">
              {format(new Date(selectedDate), 'yyyy年M月', { locale: ja })}累計
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> 売上</p>
                <p className="text-2xl font-bold text-green-600">¥{(stats?.month_total?.sales || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> 仕入れ</p>
                <p className="text-2xl font-bold text-red-600">¥{(stats?.month_total?.purchase || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3" /> 利益</p>
                <p className={`text-2xl font-bold ${(stats?.month_total?.profit || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ¥{(stats?.month_total?.profit || 0).toLocaleString()}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-slate-500 flex items-center gap-1"><Truck className="h-3 w-3" /> 配送件数</p>
                <p className="text-2xl font-bold text-slate-700">{stats?.month_total?.delivery_count || 0}件</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 入力済み仕入れ先 */}
      {(enteredSuppliers.length > 0 || customItems.length > 0) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Check className="h-4 w-4 text-emerald-600" /> 入力済み
              </CardTitle>
              <div className="text-right">
                <p className="text-sm text-slate-500">仕入れ合計</p>
                <p className="text-xl font-bold text-red-600">¥{(supplierTotal + customTotal).toLocaleString()}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {enteredSuppliers.map(s => {
                const a = purchaseAmounts[s.id];
                const total = (a.amount_8 || 0) + (a.amount_10 || 0);
                const pm = a.payment_method || s.payment_method || '';
                const ps = a.payment_status || '';
                const ref = savedRefNumbers[s.id] || a.ref_number || '';
                return (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`entered-supplier-${s.id}`}>
                    <div onClick={() => openSupplierInput(s)} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{s.name}</p>
                        {pm && <span className="text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{pm}</span>}
                        {ps && <span className={`text-xs px-1.5 py-0.5 rounded ${ps === '支払い済み' ? 'bg-green-200 text-green-800' : 'bg-orange-200 text-orange-800'}`}>{ps}</span>}
                        {ref && isCashPayment(pm) && <span className="text-xs bg-slate-900 text-white px-2 py-0.5 rounded font-mono font-bold">{ref}</span>}
                      </div>
                      <p className="text-xs text-slate-500">
                        8%: ¥{(a.amount_8 || 0).toLocaleString()} / 10%: ¥{(a.amount_10 || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold font-mono">¥{total.toLocaleString()}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteSupplierEntry(s.id); }} className="p-1 text-red-400 hover:text-red-600" title="削除">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {customItems.map(item => {
                const total = (item.amount_8 || 0) + (item.amount_10 || 0);
                const pm = item.payment_method || '';
                const ps = item.payment_status || '';
                const ref = savedRefNumbers[item.id] || item.ref_number || '';
                return (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div onClick={() => openCustomInput(item)} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{item.name}</p>
                        {pm && <span className="text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{pm}</span>}
                        {ps && <span className={`text-xs px-1.5 py-0.5 rounded ${ps === '支払い済み' ? 'bg-green-200 text-green-800' : 'bg-orange-200 text-orange-800'}`}>{ps}</span>}
                        {ref && isCashPayment(pm) && <span className="text-xs bg-slate-900 text-white px-2 py-0.5 rounded font-mono font-bold">{ref}</span>}
                      </div>
                      <p className="text-xs text-slate-500">
                        8%: ¥{(item.amount_8 || 0).toLocaleString()} / 10%: ¥{(item.amount_10 || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold font-mono">¥{total.toLocaleString()}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteCustomEntry(item.id); }} className="p-1 text-red-400 hover:text-red-600" title="削除">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 仕入れ先選択 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" /> 仕入れ先を選択
            </CardTitle>
            <Button onClick={handleSave} disabled={saving} data-testid="save-purchase-btn">
              <Save className="h-4 w-4 mr-2" />{saving ? '保存中...' : '全体を保存'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {unentered.map(supplier => (
              <button key={supplier.id} onClick={() => openSupplierInput(supplier)}
                className="flex flex-col gap-1 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
                data-testid={`supplier-card-${supplier.id}`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="font-medium text-sm truncate">{supplier.name}</span>
                </div>
                {supplier.payment_method && (
                  <span className="text-xs text-slate-400 pl-6">{supplier.payment_method}</span>
                )}
              </button>
            ))}
            <button onClick={addAndEditCustom}
              className="flex items-center gap-2 p-3 bg-white border-2 border-dashed border-slate-300 rounded-lg hover:bg-green-50 hover:border-green-400 transition-colors text-left"
              data-testid="add-custom-supplier-btn"
            >
              <Plus className="h-4 w-4 text-green-500 shrink-0" />
              <span className="text-sm text-green-600 font-medium">その他追加</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* 経費精算コーナー */}
      <Card className="border-orange-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-4 w-4 text-orange-600" /> 経費精算
            </CardTitle>
            {expenseTotal > 0 && (
              <p className="text-lg font-bold text-orange-600">¥{expenseTotal.toLocaleString()}</p>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {expenseItems.length > 0 && (
            <div className="space-y-2 mb-3">
              {expenseItems.map(item => {
                const total = (item.amount_8 || 0) + (item.amount_10 || 0);
                const ref = savedRefNumbers[item.id] || item.ref_number || '';
                return (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div onClick={() => openExpenseInput(item)} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{item.name}</p>
                        {ref && <span className="text-xs bg-slate-900 text-white px-2 py-0.5 rounded font-mono font-bold">{ref}</span>}
                      </div>
                      <p className="text-xs text-slate-500">
                        8%: ¥{(item.amount_8 || 0).toLocaleString()} / 10%: ¥{(item.amount_10 || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold font-mono">¥{total.toLocaleString()}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteExpenseEntry(item.id); }} className="p-1 text-red-400 hover:text-red-600" title="削除">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <button onClick={addAndEditExpense}
            className="flex items-center gap-2 p-3 w-full bg-white border-2 border-dashed border-orange-300 rounded-lg hover:bg-orange-50 hover:border-orange-400 transition-colors text-left"
            data-testid="add-expense-btn"
          >
            <Plus className="h-4 w-4 text-orange-500 shrink-0" />
            <span className="text-sm text-orange-600 font-medium">経費を追加</span>
          </button>
        </CardContent>
      </Card>
    </div>
  );
};
